import React, { useCallback, useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { toast } from "react-toastify";
import ConfirmDialog from "../../../components/ui/ConfirmDialog";
import {
  ActionsCell,
  Badge,
  EmptyState,
  IconButton,
  Pagination,
  SearchInput,
  TableSkeleton,
} from "../../../components/ui";
import {
  apiMessage,
  getCardDeclines,
  getFailedPayments,
  getPastDue,
  recheckPayment,
  remindPayment,
  retrySubscription,
  reviewPayment,
  syncSubscription,
} from "../../../services/monitoring";
import { formatAmount, formatDate, KIND_LABELS, PaymentDetails } from "./shared";

const REVIEW_FILTERS = [
  { value: "open", label: "To handle" },
  { value: "resolved", label: "Resolved" },
  { value: "ignored", label: "Ignored" },
  { value: "all", label: "All" },
];

const REVIEW_BADGES = {
  open: { label: "To handle", color: "amber" },
  resolved: { label: "Resolved", color: "green" },
  ignored: { label: "Ignored", color: "slate" },
};

const OUTCOMES = {
  delivered: "Accepted by the provider: the service has been delivered",
  failed: "The provider confirms the failure",
  pending: "Still waiting for validation on the phone",
  already_processed: "Already processed",
  not_found: "Unknown transaction",
  error: "The provider could not be reached",
};

/** Mobile Money payments that failed or stay pending. */
const MobileFailures = ({ onChange }) => {
  const { t } = useTranslation();
  const [review, setReview] = useState("open");
  const [search, setSearch] = useState("");
  const [page, setPage] = useState(1);
  const [data, setData] = useState(null);
  const [details, setDetails] = useState(null);
  const [busy, setBusy] = useState(null);
  const [note, setNote] = useState("");

  const load = useCallback(() => getFailedPayments({ review, q: search, page }).then(setData), [review, search, page]);
  useEffect(() => {
    const timer = setTimeout(load, search ? 300 : 0);
    return () => clearTimeout(timer);
  }, [load, search]);

  const act = async (id, action) => {
    setBusy(`${id}:${action}`);
    try {
      if (action === "recheck") {
        const { result } = await recheckPayment(id);
        (result.outcome === "delivered" ? toast.success : toast.info)(t(OUTCOMES[result.outcome] || result.outcome));
      } else if (action === "remind") {
        await remindPayment(id);
        toast.success(t("Reminder sent to the customer"));
      } else {
        await reviewPayment(id, action, note);
        toast.success(t("Follow-up updated"));
        setNote("");
      }
      load();
      onChange && onChange();
      if (action !== "recheck" && action !== "remind") setDetails(null);
    } catch (error) {
      toast.error(apiMessage(error, t("Something went wrong")));
    }
    setBusy(null);
  };

  const items = data?.items || [];
  return (
    <section className="ui-card">
      <div className="ui-toolbar">
        <div className="ui-chips">
          {REVIEW_FILTERS.map((f) => (
            <button key={f.value} type="button" className="ui-chip" aria-pressed={review === f.value} onClick={() => { setReview(f.value); setPage(1); }}>
              {t(f.label)}
            </button>
          ))}
        </div>
        <SearchInput value={search} onChange={(v) => { setSearch(v); setPage(1); }} placeholder={t("Transaction, name or email")} />
      </div>
      {!data ? (
        <TableSkeleton />
      ) : !items.length ? (
        <EmptyState icon="ri-checkbox-circle-line">{review === "open" ? t("No failed payment to handle") : t("Nothing here")}</EmptyState>
      ) : (
        <div className="ui-table-wrap">
          <table className="ui-table">
            <thead>
              <tr>
                <th>{t("Customer")}</th>
                <th>{t("Payment")}</th>
                <th>{t("Reason")}</th>
                <th>{t("Follow-up")}</th>
                <th>{t("Date")}</th>
                <th aria-label={t("Actions")}></th>
              </tr>
            </thead>
            <tbody>
              {items.map((p) => (
                <tr key={p.id} className="ui-row-link" onClick={() => setDetails(p.id)}>
                  <td>
                    <span className="ui-media-title">{p.customer?.name}</span>
                    <span className="ui-media-meta">{p.customer?.email || p.phone}</span>
                  </td>
                  <td>
                    <strong>{formatAmount(p.amount, p.currency)}</strong>
                    <span className="ui-media-meta">
                      {t(KIND_LABELS[p.kind] || p.kind)}
                      {p.operator ? ` · ${p.operator}` : ""}
                    </span>
                  </td>
                  <td>
                    {p.status === "PENDING" ? <Badge color="blue" dot>{t("Pending")}</Badge> : <Badge color="red" dot>{t("Failed")}</Badge>}
                    <span className="ui-media-meta logs-reason">{t(p.reason)}</span>
                  </td>
                  <td>
                    <Badge color={REVIEW_BADGES[p.review?.status || "open"].color}>{t(REVIEW_BADGES[p.review?.status || "open"].label)}</Badge>
                    {p.reminderSentAt && <span className="ui-media-meta">{t("Reminded")} {formatDate(p.reminderSentAt)}</span>}
                  </td>
                  <td className="ui-cell-muted">{formatDate(p.createdAt)}</td>
                  <ActionsCell>
                    <IconButton icon="ri-refresh-line" label={t("Recheck with the provider")} onClick={() => act(p.id, "recheck")} />
                    {p.customer?.id && <IconButton icon="ri-mail-send-line" label={t("Send a reminder")} onClick={() => act(p.id, "remind")} />}
                    {p.review?.status !== "resolved" && <IconButton icon="ri-check-double-line" label={t("Mark as resolved")} onClick={() => act(p.id, "resolved")} />}
                  </ActionsCell>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
      {data && <Pagination page={page} pageCount={Math.max(1, Math.ceil(data.total / data.pageSize))} total={data.total} pageSize={data.pageSize} onChange={setPage} />}

      <PaymentDetails paymentId={details} onClose={() => setDetails(null)}>
        {(p) => (
          <div className="logs-actions-box">
            <div className="logs-actions">
              <button type="button" className="ui-btn ui-btn-secondary" disabled={Boolean(busy)} onClick={() => act(p.id, "recheck")}>
                <i className="ri-refresh-line" aria-hidden="true"></i>
                {busy === `${p.id}:recheck` ? t("Checking…") : t("Recheck with the provider")}
              </button>
              {p.customer?.id && (
                <button type="button" className="ui-btn ui-btn-secondary" disabled={Boolean(busy)} onClick={() => act(p.id, "remind")}>
                  <i className="ri-mail-send-line" aria-hidden="true"></i>
                  {t("Send a reminder")}
                </button>
              )}
            </div>
            <div className="ui-field">
              <label htmlFor="logs-note">{t("Internal note")}</label>
              <input id="logs-note" className="ui-input" value={note} maxLength={500} placeholder={p.review?.note || t("e.g. Customer called, paid by card")} onChange={(e) => setNote(e.target.value)} />
            </div>
            <div className="logs-actions">
              <button type="button" className="ui-btn" onClick={() => act(p.id, "resolved")}>
                <i className="ri-check-double-line" aria-hidden="true"></i>
                {t("Mark as resolved")}
              </button>
              <button type="button" className="ui-btn ui-btn-secondary" onClick={() => act(p.id, "ignored")}>
                <i className="ri-eye-off-line" aria-hidden="true"></i>
                {t("Ignore")}
              </button>
              {p.review?.status && p.review.status !== "open" && (
                <button type="button" className="ui-btn ui-btn-secondary" onClick={() => act(p.id, "open")}>
                  {t("Reopen")}
                </button>
              )}
            </div>
          </div>
        )}
      </PaymentDetails>
    </section>
  );
};

/** Card subscriptions whose renewal was declined (grace period or suspended). */
const PastDue = ({ onChange }) => {
  const { t } = useTranslation();
  const [items, setItems] = useState(null);
  const [confirm, setConfirm] = useState(null);
  const load = useCallback(() => getPastDue().then(setItems), []);
  useEffect(() => {
    load();
  }, [load]);

  const run = async (item, action) => {
    try {
      const result = action === "retry" ? await retrySubscription(item.id) : await syncSubscription(item.id);
      const messages = {
        paid: t("Paid: the subscription is up to date"),
        synced: t("Synchronised with Stripe"),
        nothing_due: t("Nothing to pay"),
        nothing_to_sync: t("No Stripe subscription"),
        requires_action: t("The bank asks the customer for a validation (3D Secure)"),
      };
      const fn = result.status === "failed" ? toast.error : result.status === "paid" || result.status === "synced" ? toast.success : toast.info;
      fn(result.status === "failed" ? `${t("Declined")} : ${result.error || ""}` : messages[result.status] || result.status);
      load();
      onChange && onChange();
    } catch (error) {
      toast.error(apiMessage(error, t("Something went wrong")));
    }
  };

  return (
    <section className="ui-card">
      {!items ? (
        <TableSkeleton />
      ) : !items.length ? (
        <EmptyState icon="ri-checkbox-circle-line">{t("No unpaid card subscription")}</EmptyState>
      ) : (
        <div className="ui-table-wrap">
          <table className="ui-table">
            <thead>
              <tr>
                <th>{t("Customer")}</th>
                <th>{t("Plan")}</th>
                <th>{t("Bank reason")}</th>
                <th>{t("Access")}</th>
                <th>{t("Reminders")}</th>
                <th aria-label={t("Actions")}></th>
              </tr>
            </thead>
            <tbody>
              {items.map((s) => (
                <tr key={s.id}>
                  <td>
                    <span className="ui-media-title">{s.customer?.name || "—"}</span>
                    <span className="ui-media-meta">{s.customer?.email}</span>
                  </td>
                  <td>{s.plan === "annual" ? t("Annual") : t("Monthly")}</td>
                  <td>
                    <span className="ui-clamp" title={s.reason}>{s.reason || "—"}</span>
                    <span className="ui-media-meta">{t("Since")} {formatDate(s.since)}</span>
                  </td>
                  <td>
                    {s.state === "suspended" ? (
                      <Badge color="red" dot>{t("Suspended")}</Badge>
                    ) : (
                      <Badge color="amber" dot>{t("Grace until")} {formatDate(s.graceUntil)}</Badge>
                    )}
                  </td>
                  <td className="ui-cell-muted">{s.remindersSent}</td>
                  <ActionsCell>
                    <IconButton icon="ri-refresh-line" label={t("Synchronise with Stripe")} onClick={() => run(s, "sync")} />
                    <IconButton icon="ri-bank-card-line" label={t("Charge the card again")} onClick={() => setConfirm(s)} />
                    {s.invoiceUrl && (
                      <a className="ui-icon-btn" href={s.invoiceUrl} target="_blank" rel="noreferrer" title={t("Stripe invoice")} aria-label={t("Stripe invoice")}>
                        <i className="ri-file-list-2-line" aria-hidden="true"></i>
                      </a>
                    )}
                  </ActionsCell>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
      <p className="ui-hint logs-note">
        {t("Stripe retries automatically and the customer is reminded at D+3, the day before the end of the grace period, then at suspension.")}
      </p>
      <ConfirmDialog open={Boolean(confirm)} onClose={() => setConfirm(null)} onConfirm={() => run(confirm, "retry")} confirmLabel={t("Charge the card again")}>
        <strong>{t("Charge the customer's card now?")}</strong>
        <p>{t("The open invoice is charged to the default card. If the bank declines, nothing changes.")}</p>
      </ConfirmDialog>
    </section>
  );
};

/** Declined card payments (from Stripe webhooks). */
const CardDeclines = () => {
  const { t } = useTranslation();
  const [page, setPage] = useState(1);
  const [data, setData] = useState(null);
  useEffect(() => {
    getCardDeclines({ page }).then(setData);
  }, [page]);
  const items = data?.items || [];
  return (
    <section className="ui-card">
      {!data ? (
        <TableSkeleton />
      ) : !items.length ? (
        <EmptyState icon="ri-checkbox-circle-line">{t("No declined card in the last 30 days")}</EmptyState>
      ) : (
        <div className="ui-table-wrap">
          <table className="ui-table">
            <thead>
              <tr>
                <th>{t("Customer")}</th>
                <th>{t("Payment")}</th>
                <th>{t("Reason")}</th>
                <th>{t("Date")}</th>
              </tr>
            </thead>
            <tbody>
              {items.map((d) => (
                <tr key={d.id}>
                  <td>
                    <span className="ui-media-title">{d.customer?.name || "—"}</span>
                    <span className="ui-media-meta">{d.customer?.email}</span>
                  </td>
                  <td>
                    <strong>{formatAmount(d.amount, d.currency)}</strong>
                    <span className="ui-media-meta">
                      {t(KIND_LABELS[d.kind] || d.kind || "")}
                      {d.livemode === false ? ` · ${t("test mode")}` : ""}
                    </span>
                  </td>
                  <td>
                    <span className="ui-clamp" title={d.message}>{t(d.reason)}</span>
                    {d.attempt > 1 && <span className="ui-media-meta">{t("Attempt")} {d.attempt}</span>}
                  </td>
                  <td className="ui-cell-muted">{formatDate(d.at)}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
      {data && <Pagination page={page} pageCount={Math.max(1, Math.ceil(data.total / data.pageSize))} total={data.total} pageSize={data.pageSize} onChange={setPage} />}
    </section>
  );
};

const SECTIONS = [
  { value: "mobile", label: "Mobile Money", icon: "ri-smartphone-line" },
  { value: "pastdue", label: "Unpaid card subscriptions", icon: "ri-bank-card-line" },
  { value: "declines", label: "Declined cards", icon: "ri-close-line" },
];

const FailedPayments = ({ stats, onChange }) => {
  const { t } = useTranslation();
  const [section, setSection] = useState("mobile");
  const counts = { mobile: stats?.toReview, pastdue: stats?.pastDue, declines: stats?.cardDeclined };
  return (
    <div className="logs-section">
      {stats && (
        <div className="logs-insight ui-card">
          <div>
            <span className="ui-label">{t("Mobile Money failure rate (30 days)")}</span>
            <strong data-tone={stats.mobile.failureRate > 30 ? "red" : stats.mobile.failureRate > 15 ? "amber" : "green"}>{stats.mobile.failureRate} %</strong>
            <span className="ui-hint">
              {stats.mobile.success} {t("succeeded")} · {stats.mobile.failed} {t("failed")} · {stats.mobile.pending} {t("pending")}
            </span>
          </div>
          <div className="logs-reasons">
            <span className="ui-label">{t("Main reasons")}</span>
            {stats.topReasons.length ? (
              stats.topReasons.map((r) => (
                <div key={r.code} className="logs-reason-bar">
                  <span>{t(r.label)}</span>
                  <span className="logs-bar" style={{ "--w": `${Math.round((r.count / Math.max(1, stats.topReasons[0].count)) * 100)}%` }} />
                  <strong>{r.count}</strong>
                </div>
              ))
            ) : (
              <span className="ui-hint">{t("No failure")}</span>
            )}
          </div>
          <div>
            <span className="ui-label">{t("Unpaid settled (30 days)")}</span>
            <strong data-tone="green">{stats.recovered}</strong>
            <span className="ui-hint">{t("card renewals recovered after a decline")}</span>
          </div>
        </div>
      )}
      <div className="ui-chips logs-subnav">
        {SECTIONS.map((s) => (
          <button key={s.value} type="button" className="ui-chip" aria-pressed={section === s.value} onClick={() => setSection(s.value)}>
            <i className={s.icon} aria-hidden="true"></i> {t(s.label)}
            {counts[s.value] > 0 && <span className="logs-count">{counts[s.value]}</span>}
          </button>
        ))}
      </div>
      {section === "mobile" && <MobileFailures onChange={onChange} />}
      {section === "pastdue" && <PastDue onChange={onChange} />}
      {section === "declines" && <CardDeclines />}
    </div>
  );
};

export default FailedPayments;
